export interface CropMetadata {
  enabled: boolean;
  active: boolean;
  x: number;
  y: number;
  scale: number;
  maskWidth: number;
  maskHeight: number;
}

export interface CropDesignItem {
  id: string;
  type: string;
  top: number;
  left: number;
  width: number;
  height: number;
  src?: string;
  imageCropper?: CropMetadata;
  [key: string]: unknown;
}

export const MIN_MASK_SIZE = 24;

export function isCropTarget(item: unknown): item is CropDesignItem {
  if (!item || typeof item !== 'object') return false;
  const type = String((item as Record<string, unknown>).type ?? '');
  return type === 'image' || type === 'svg';
}

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

export interface NormalizedCropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const FULL_CROP: NormalizedCropRect = { x: 0, y: 0, width: 1, height: 1 };

export interface ItemBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * New item box after re-cropping, keeping the visible pixels where they are (Canva-style).
 *
 * The box shows `prev` of the source, so one source unit spans `box / prev` canvas px. Keeping
 * that scale means a tighter crop shrinks the box instead of blowing the remaining pixels up to
 * the old size (which silently lowered print DPI and drifted on every re-crop).
 */
export function croppedItemBox(box: ItemBox, prev: NormalizedCropRect, next: NormalizedCropRect): ItemBox {
  const pw = Math.max(1e-6, prev.width);
  const ph = Math.max(1e-6, prev.height);
  const unitX = Math.max(1, box.width) / pw;
  const unitY = Math.max(1, box.height) / ph;
  return {
    left: box.left + (next.x - prev.x) * unitX,
    top: box.top + (next.y - prev.y) * unitY,
    width: Math.max(1, next.width * unitX),
    height: Math.max(1, next.height * unitY),
  };
}

/** Intrinsic size of SVG markup: width/height attributes, else the viewBox. */
export function svgIntrinsicSize(markup: unknown): { width: number; height: number } | null {
  if (typeof markup !== 'string') return null;
  const tag = markup.match(/<svg\b[^>]*>/i)?.[0];
  if (!tag) return null;
  const attr = (name: string) => tag.match(new RegExp(`\\s${name}\\s*=\\s*["']([^"']*)["']`, 'i'))?.[1] ?? '';
  const w = parseFloat(attr('width'));
  const h = parseFloat(attr('height'));
  if (w > 0 && h > 0 && !/%/.test(attr('width') + attr('height'))) return { width: w, height: h };
  const box = attr('viewBox').trim().split(/[\s,]+/).map(Number);
  if (box.length === 4 && box[2] > 0 && box[3] > 0) return { width: box[2], height: box[3] };
  return null;
}

/** True when two sizes share an aspect ratio within `tolerance` (relative). */
export function sameAspect(aw: number, ah: number, bw: number, bh: number, tolerance = 0.02): boolean {
  if (!(aw > 0 && ah > 0 && bw > 0 && bh > 0)) return true;
  const a = aw / ah;
  const b = bw / bh;
  return Math.abs(a - b) / b <= tolerance;
}

/** Print resolution of an image, as served by the Preflight addon's `image.print.dpi` filter. */
export interface PrintDpi {
  actual: number;
  target: number;
  min: number;
}

export type DpiLevel = 'ok' | 'warn' | 'low';

/** `ok` meets the target, `warn` is between the minimum and the target, `low` is below the minimum. */
export function dpiLevel(dpi: PrintDpi): DpiLevel {
  if (dpi.actual >= dpi.target) return 'ok';
  return dpi.actual >= dpi.min ? 'warn' : 'low';
}

export function isPrintDpi(value: unknown): value is PrintDpi {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return [v.actual, v.target, v.min].every((n) => typeof n === 'number' && Number.isFinite(n) && n > 0);
}
